import {
  Box,
  Flex,
  HStack,
  Image,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LAUNCH_TOKEN_FEE_AGENTFUN } from '@cronos-app/sdk';
import { ButtonWithAuthentication } from '@ui/components/ButtonWithAuthentication';
import { MaskIcon } from '@ui/components/MaskIcon';
import { Num } from '@ui/components/Num';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { Token } from '@ui/config/token';
import { useAgentFunBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useTokenPrice } from '@ui/hooks/tokenPrice';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCreateAgentModal } from './useCreateAgentModal';
import { useFinalizePurchase } from './useFinalizePurchase';

export const CreateAgentFooter: FC = () => {
  const step = useCreateAgentModal((s) => s.step);
  const CTA = step === 'Purchase' ? <FinalizeButton /> : <ProceedButton />;
  const Info =
    step === 'Purchase' ? (
      <Total />
    ) : (
      <Flex flexDir="column" gap={2}>
        <CreationFee />
        <Balance />
      </Flex>
    );

  return (
    <Box w="full">
      {Info}
      {CTA}
    </Box>
  );
};

const FinalizeButton: FC = () => {
  const { handleClick, isLoading, disabled } = useFinalizePurchase();

  return (
    <ButtonWithAuthentication
      w="full"
      onClick={handleClick}
      mt={2}
      disabled={disabled}
    >
      {isLoading && <Spinner color="currentcolor" />}
      {!isLoading && 'Create Agent!'}
    </ButtonWithAuthentication>
  );
};

const ProceedButton: FC = () => {
  const { formState } = useFormContext();
  const { data: agentfunBalance } = useAgentFunBalance();
  const disabled =
    !agentfunBalance ||
    agentfunBalance.lt(LAUNCH_TOKEN_FEE_AGENTFUN) ||
    !formState.isValid;

  return (
    <ButtonWithAuthentication
      mt={2}
      onClick={() => useCreateAgentModal.getState().setNextStep()}
      w="full"
      disabled={disabled}
    >
      Next
      <MaskIcon
        icon="url(/masks/chevron.svg)"
        bgColor="currentcolor"
        boxSize="24px"
        transform="rotate(270deg)"
      />
    </ButtonWithAuthentication>
  );
};

const Balance: FC = () => {
  const balance = useAgentFunBalance().data;
  const price = useTokenPrice({ token: Token.agentFun }).data?.usd_price;

  return (
    <HStack textStyle="body2" justify="space-between" align="flex-start">
      <Text>Balance</Text>
      <VStack align="flex-end" fontWeight="bold" gap={0}>
        <HStack gap={0.5}>
          <Num value={balance} />
          <Image alt="Agent FUN" src="/tokens/agentfun.svg" boxSize="16px" />
          <Flex textStyle="body3">
            (
            <Num
              display="inline"
              decimalPlaces={2}
              prefix="$"
              value={balance ? price?.mul(balance) : undefined}
            />
            )
          </Flex>
        </HStack>
      </VStack>
    </HStack>
  );
};

const CreationFee: FC = () => {
  const chainLayer = useGlobalChainLayer();
  const price = useTokenPrice({ token: Token.agentFun }).data?.usd_price;
  return (
    <HStack textStyle="body2" justify="space-between" align="flex-start">
      <Text>Creation fee</Text>
      <VStack gap={0} align="flex-end">
        <HStack gap={0.5} fontWeight="bold">
          {LAUNCH_TOKEN_FEE_AGENTFUN}
          <Image alt="Agent FUN" src="/tokens/agentfun.svg" boxSize="16px" />
          <Flex textStyle="body3">
            (
            <Num
              display="inline"
              decimalPlaces={2}
              prefix="$"
              value={price?.mul(LAUNCH_TOKEN_FEE_AGENTFUN)}
            />
            )
          </Flex>
        </HStack>
        <Link
          textStyle="caption"
          color="primary"
          href={EXTERNAL_LINK.swapAgentFun(chainLayer)}
          target="_blank"
          position="relative"
        >
          Buy AGENTFUN
          <MaskIcon icon="url(/masks/arrow-outward.svg)" boxSize="16px" />
        </Link>
      </VStack>
    </HStack>
  );
};

const Total: FC = () => {
  const enteredAmount = useCreateAgentModal((s) => s.buyAmount);
  const price = useTokenPrice({ token: Token.agentFun }).data?.usd_price;
  const totalAmount = enteredAmount.add(LAUNCH_TOKEN_FEE_AGENTFUN);

  return (
    <HStack textStyle="body2" justify="space-between">
      <Text>Total</Text>
      <HStack fontWeight="bold">
        <Num value={totalAmount} />
        <Image alt="Agent FUN" src="/tokens/agentfun.svg" boxSize="16px" />
        <Flex textStyle="body3">
          (
          <Num
            display="inline"
            decimalPlaces={2}
            prefix="$"
            value={price?.mul(totalAmount)}
          />
          )
        </Flex>
      </HStack>
    </HStack>
  );
};
